/**
 * Stripe Service - Frontend integration with Supabase Edge Functions
 * Handles checkout, portal, subscription management, and event publishing
 * Aligned with PRD-integration-paiement-abonnements.md v1.4
 */

import { supabase } from '../config/supabase';
import type { CheckCanPublishResponse, PublishEventResponse } from '../types/supabase.types';

// =============================================
// Configuration
// =============================================

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

// Plan IDs according to PRD: free, monthly, yearly, pay_per_event
export type PlanId = 'free' | 'monthly' | 'yearly' | 'pay_per_event';

// Price IDs from environment
const PRICE_IDS: Record<PlanId, string | null> = {
  free: null,
  monthly: process.env.REACT_APP_STRIPE_PRICE_PRO_MONTHLY || '',
  yearly: process.env.REACT_APP_STRIPE_PRICE_PRO_YEARLY || '',
  pay_per_event: process.env.REACT_APP_STRIPE_PRICE_PAY_PER_EVENT || '',
};

// =============================================
// Types
// =============================================

export interface CheckoutResult {
  success: boolean;
  sessionId?: string;
  url?: string;
  error?: string;
}

export interface PortalResult {
  success: boolean;
  url?: string;
  error?: string;
}

export interface SubscriptionInfo {
  planId: PlanId;
  status: 'active' | 'canceled' | 'past_due' | 'incomplete' | 'trialing';
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
  eventsUsed: number;
  eventsLimit: number | null; // null = unlimited (Pro)
  // stripeCustomerId removed - not needed in frontend (security improvement)
}

export interface PublishResult {
  success: boolean;
  action?: string;
  error?: string;
  eventsUsed?: number;
  eventsLimit?: number;
}

export interface CanPublishResult {
  canPublish: boolean;
  reason: string;
  eventsUsed?: number;
  eventsLimit?: number;
}

// =============================================
// Checkout Functions
// =============================================

/**
 * Create a Stripe Checkout session for subscription or pay-per-event
 * @param planId - The plan to purchase (monthly, yearly, pay_per_event)
 * @param organizerId - The organizer's ID
 * @param eventId - Optional: The event to publish after pay-per-event payment
 * @param referrerEmail - Optional: Email of the referrer for referral system
 */
export async function createCheckoutSession(
  planId: PlanId,
  organizerId: string,
  eventId?: string,
  referrerEmail?: string
): Promise<CheckoutResult> {
  const priceId = PRICE_IDS[planId];

  if (!priceId) {
    return { success: false, error: 'Invalid plan selected' };
  }

  try {
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

    // Debug log without sensitive user data
    if (process.env.NODE_ENV === 'development') {
      console.log('[stripe.service] Session check:', {
        hasSession: !!sessionData?.session,
        sessionError: sessionError?.message,
        // userId removed from logs for security
        hasExpiry: !!sessionData?.session?.expires_at,
      });
    }

    if (!sessionData.session) {
      console.error('[stripe.service] No session found');
      return { success: false, error: 'Not authenticated' };
    }

    const baseUrl = window.location.origin;
    // Include payment type in URL for proper success message
    const paymentType = planId === 'pay_per_event' ? 'pay_per_event' : 'subscription';
    const successUrl = `${baseUrl}/organizer/subscription/success?session_id={CHECKOUT_SESSION_ID}&type=${paymentType}`;
    const cancelUrl = `${baseUrl}/organizer/subscription/cancel`;

    console.log('[stripe.service] Calling create-checkout with:', {
      priceId,
      planId,
      organizerId,
      hasEventId: !!eventId,
      successUrl,
      cancelUrl,
    });

    const { data, error } = await supabase.functions.invoke('create-checkout', {
      body: {
        priceId,
        planId, // Pour metadata webhook
        organizerId,
        eventId, // Pour pay-per-event
        referrerEmail, // Pour système de parrainage
        successUrl,
        cancelUrl,
      },
    });

    console.log('[stripe.service] create-checkout response:', { data, error });

    if (error) {
      // Try to get detailed error from response context
      let errorDetails = error.message;
      try {
        if (error.context && typeof error.context.json === 'function') {
          const errorBody = await error.context.json();
          console.error('[stripe.service] Error response body:', errorBody);
          errorDetails = errorBody.error || errorBody.message || error.message;
        }
      } catch {
        console.error('[stripe.service] Could not parse error context');
      }

      console.error('[stripe.service] Checkout error details:', {
        message: error.message,
        name: error.name,
        details: errorDetails,
        status: error.context?.status,
      });
      return { success: false, error: errorDetails };
    }

    return {
      success: true,
      sessionId: data.sessionId,
      url: data.url,
    };
  } catch (err) {
    console.error('Checkout error:', err);
    return { success: false, error: (err as Error).message };
  }
}

/**
 * Redirect to Stripe Checkout
 */
export async function redirectToCheckout(
  planId: PlanId,
  organizerId: string,
  eventId?: string,
  referrerEmail?: string
): Promise<void> {
  const result = await createCheckoutSession(planId, organizerId, eventId, referrerEmail);

  if (!result.success) {
    throw new Error(result.error || 'Failed to create checkout session');
  }

  if (result.url) {
    window.location.href = result.url;
  }
}

// =============================================
// Customer Portal Functions
// =============================================

/**
 * Create a Stripe Customer Portal session
 */
export async function createPortalSession(
  organizerId: string
): Promise<PortalResult> {
  try {
    const { data: sessionData } = await supabase.auth.getSession();

    if (!sessionData.session) {
      return { success: false, error: 'Not authenticated' };
    }

    const returnUrl = `${window.location.origin}/organizer/dashboard`;

    const { data, error } = await supabase.functions.invoke('create-portal', {
      body: {
        organizerId,
        returnUrl,
      },
    });

    if (error) {
      console.error('Portal error:', error);
      return { success: false, error: error.message };
    }

    return {
      success: true,
      url: data.url,
    };
  } catch (err) {
    console.error('Portal error:', err);
    return { success: false, error: (err as Error).message };
  }
}

/**
 * Redirect to Stripe Customer Portal
 */
export async function redirectToPortal(organizerId: string): Promise<void> {
  const result = await createPortalSession(organizerId);

  if (!result.success) {
    throw new Error(result.error || 'Failed to create portal session');
  }

  if (result.url) {
    window.location.href = result.url;
  }
}

// =============================================
// Subscription Info Functions
// =============================================

// Type for RPC response (get_my_subscription_info)
// PRD: PRD-securite-organizers-rpc.md
interface SubscriptionRpcResponse {
  plan_id: string | null;
  subscription_status: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean | null;
  events_used: number | null;
  events_limit: number | null;
}

/**
 * Get organizer's subscription info via secure RPC
 * PRD: PRD-securite-organizers-rpc.md
 *
 * Uses RPC function that:
 * - Verifies user is authenticated
 * - Returns ONLY the authenticated user's data
 * - Never exposes stripe_customer_id or stripe_subscription_id
 *
 * @param organizerId - Kept for API compatibility but not used (RPC uses auth.uid())
 */
export async function getSubscriptionInfo(
  organizerId: string
): Promise<SubscriptionInfo | null> {
  try {
    // Use secure RPC instead of direct SELECT
    // The RPC automatically uses auth.uid() - organizerId param kept for compatibility
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase.rpc as any)('get_my_subscription_info');

    if (error) {
      console.error('Error fetching subscription via RPC:', error);
      return null;
    }

    // RPC returns an array, get first result
    const rpcData = Array.isArray(data) ? data[0] : data;

    if (!rpcData) {
      // No organizer record found for this user
      return null;
    }

    const orgData = rpcData as SubscriptionRpcResponse;

    return {
      planId: (orgData.plan_id || 'free') as PlanId,
      status: (orgData.subscription_status || 'active') as SubscriptionInfo['status'],
      currentPeriodEnd: orgData.current_period_end
        ? new Date(orgData.current_period_end)
        : null,
      cancelAtPeriodEnd: orgData.cancel_at_period_end || false,
      eventsUsed: orgData.events_used || 0,
      eventsLimit: orgData.events_limit ?? null, // null = unlimited
    };
  } catch (err) {
    console.error('Error fetching subscription:', err);
    return null;
  }
}

// =============================================
// Event Publishing Functions (PRD aligned)
// =============================================

// RPC response types are imported from supabase.types.ts

/**
 * Check if an event can be published
 * Uses RPC check_can_publish for server-side verification
 */
export async function checkCanPublish(eventId: string): Promise<CanPublishResult> {
  try {
    const { data, error } = await supabase.rpc('check_can_publish', {
      p_event_id: eventId,
    });

    if (error) {
      console.error('Error checking publish permission:', error);
      return {
        canPublish: false,
        reason: 'error',
      };
    }

    const result = data as unknown as CheckCanPublishResponse;

    return {
      canPublish: result.can_publish,
      reason: result.reason,
      eventsUsed: result.events_used,
      eventsLimit: result.events_limit,
    };
  } catch (err) {
    console.error('Error checking publish permission:', err);
    return {
      canPublish: false,
      reason: 'error',
    };
  }
}

/**
 * Publish an event (server-side verified)
 * Uses RPC publish_event which handles:
 * - Simple events
 * - Series events (counts as 1)
 * - Republication (free)
 * - Limit checking
 */
export async function publishEvent(eventId: string): Promise<PublishResult> {
  try {
    const { data, error } = await supabase.rpc('publish_event', {
      p_event_id: eventId,
    });

    if (error) {
      console.error('Error publishing event:', error);
      return {
        success: false,
        error: error.message,
      };
    }

    const result = data as unknown as PublishEventResponse;

    return {
      success: result.success,
      action: result.action,
      error: result.error,
      eventsUsed: result.events_used,
      eventsLimit: result.events_limit,
    };
  } catch (err) {
    console.error('Error publishing event:', err);
    return {
      success: false,
      error: (err as Error).message,
    };
  }
}

/**
 * Publish an entire series (server-side verified)
 * Series counts as 1 event in quota
 */
export async function publishSeries(seriesId: string): Promise<PublishResult> {
  try {
    const { data, error } = await supabase.rpc('publish_series', {
      p_series_id: seriesId,
    });

    if (error) {
      console.error('Error publishing series:', error);
      return {
        success: false,
        error: error.message,
      };
    }

    const result = data as unknown as PublishEventResponse;

    return {
      success: result.success,
      action: result.action,
      error: result.error,
      eventsUsed: result.events_used,
      eventsLimit: result.events_limit,
    };
  } catch (err) {
    console.error('Error publishing series:', err);
    return {
      success: false,
      error: (err as Error).message,
    };
  }
}

// =============================================
// Utility Functions
// =============================================

/**
 * Check if Stripe is properly configured
 */
export function isStripeConfigured(): boolean {
  return Boolean(STRIPE_PUBLISHABLE_KEY && PRICE_IDS.monthly);
}

/**
 * Get price ID for a plan
 */
export function getPriceId(planId: PlanId): string | null {
  return PRICE_IDS[planId];
}

/**
 * Check if plan is Pro (unlimited events)
 */
export function isPro(planId: PlanId): boolean {
  return planId === 'monthly' || planId === 'yearly';
}

/**
 * Check if user has reached their event limit
 */
export function hasReachedLimit(eventsUsed: number, eventsLimit: number | null): boolean {
  if (eventsLimit === null) return false; // Unlimited
  return eventsUsed >= eventsLimit;
}

// =============================================
// Export default service object
// =============================================

const stripeService = {
  // Checkout
  createCheckoutSession,
  redirectToCheckout,
  // Portal
  createPortalSession,
  redirectToPortal,
  // Subscription info
  getSubscriptionInfo,
  // Event publishing (PRD)
  checkCanPublish,
  publishEvent,
  publishSeries,
  // Utils
  isStripeConfigured,
  getPriceId,
  isPro,
  hasReachedLimit,
};

export default stripeService;
